import React from "react";
import { Link } from "react-router-dom";
import { FileText, Calendar, DollarSign } from "lucide-react";
import SEO from "../components/SEO";

export const SelfAssessmentCompliance = () => {
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Self Assessment Compliance: Avoid Costly Errors",
    "description": "Complete Self Assessment compliance guide for UK taxpayers. Registration deadlines, what to declare, allowable expenses, payment on account explained.",
    "author": { "@type": "Organization", "name": "Brightson Accounting" },
    "publisher": { "@type": "Organization", "name": "Brightson Accounting" },
    "datePublished": "2026-04-06"
  };

  return (
    <>
      <SEO
        title="Self Assessment Compliance UK | Avoid Errors | Brightson Accounting"
        description="Self Assessment compliance guide: registration deadlines, income declarations, allowable expenses, payment on account. Expert advice from Wolverhampton accountants."
        canonical="/blog/self-assessment-compliance"
        schema={articleSchema}
      />

      <article className="blog-article">
        <header className="article-header">
          <div className="container">
            <div className="article-meta">
              <span className="article-category"><FileText size={16} /> Staying Compliant</span>
              <span className="article-date">6 April 2026</span>
              <span className="article-read-time">5 min read</span>
            </div>

            <h1>Self Assessment Compliance: Avoid Costly Errors</h1>
            <p className="article-subtitle">
              Complete guide to Self Assessment compliance — registration, filing, and avoiding the most common mistakes
            </p>

            <div className="article-author">
              <div className="author-info">
                <strong>Brightson Accounting (FCCA Qualified)</strong>
                <span>Self Assessment Specialists | Wolverhampton</span>
              </div>
            </div>
          </div>
        </header>

        <section className="article-content">
          <div className="container">
            <div className="article-body">
              
              <p className="article-lead">
                Many first-time filers underestimate Self Assessment compliance requirements. At Brightson Accounting, we help individuals across Wolverhampton and the West Midlands avoid the most common — and costly — errors.
              </p>

              <div className="info-box">
                <strong>Quick Summary</strong>
                <ul>
                  <li>Register by 5 October if you're self-employed or have untaxed income</li>
                  <li>File online by 31 January (paper by 31 October)</li>
                  <li>Declare ALL income, including side hustles and rental income</li>
                  <li>Only claim allowable business expenses</li>
                  <li>Payment on account means paying tax in advance for next year</li>
                </ul>
              </div>

              <h2>Who Must File a Self Assessment?</h2>

              <p>You must file a Self Assessment tax return if you:</p>
              <ul>
                <li>Are self-employed (sole trader or partner)</li>
                <li>Are a company director</li>
                <li>Have rental income over £1,000/year</li>
                <li>Have untaxed income over £2,500</li>
                <li>Earn over £100,000/year (from any source)</li>
                <li>Receive dividends and your total income exceeds personal allowance</li>
                <li>Have capital gains above the annual exempt amount</li>
              </ul>

              <p className="highlight-note">
                Not sure if you need to file? <Link to="/contact">Speak to an accountant</Link> to confirm your obligations.
              </p>

              <h2>Registration Deadlines</h2>

              <p>
                <strong>5 October:</strong> Deadline to register for Self Assessment if you're newly self-employed or have new untaxed income.
              </p>

              <p>Example: Started self-employment in May 2026 → Register by 5 October 2026.</p>

              <p>
                <strong>Late Registration Penalty:</strong> £100+ if you miss the deadline (even if you owe no tax).
              </p>

              <h2>Filing Deadlines</h2>

              <table style={{ width: '100%', marginTop: '1rem', marginBottom: '2rem', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f3f4f6', borderBottom: '2px solid #d1d5db' }}>
                    <th style={{ padding: '0.75rem', textAlign: 'left' }}>Deadline</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left' }}>Requirement</th>
                  </tr>
                </thead>
                <tbody>
                  <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <td style={{ padding: '0.75rem' }}><strong>31 October 2026</strong></td>
                    <td style={{ padding: '0.75rem' }}>Paper return deadline</td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <td style={{ padding: '0.75rem' }}><strong>31 January 2027</strong></td>
                    <td style={{ padding: '0.75rem' }}>Online return + payment deadline</td>
                  </tr>
                  <tr>
                    <td style={{ padding: '0.75rem' }}><strong>31 July 2027</strong></td>
                    <td style={{ padding: '0.75rem' }}>Second payment on account</td>
                  </tr>
                </tbody>
              </table>

              <p>Full guide: <Link to="/blog/hmrc-deadlines-uk">HMRC Deadlines You Cannot Afford to Miss</Link></p>

              {/* Mid-Article CTA */}
              <div className="article-cta-box" style={{ background: '#f8f9fa', border: '2px solid #0066cc', marginTop: '2rem', marginBottom: '2rem' }}>
                <h3>💡 Want to see how much tax you could save?</h3>
                <p>Most UK business owners are overpaying without realising it.</p>
                <Link to="/corporation-tax-calculator" className="article-cta-button">
                  👉 Try the Corporation Tax Calculator
                </Link>
              </div>

              <h2>What to Declare</h2>

              <p>You must declare ALL income, including:</p>

              <h3>Self-Employment Income</h3>
              <ul>
                <li>All business income (sales, invoices, cash)</li>
                <li>Side hustles and freelance work</li>
                <li>Gig economy earnings (Uber, Deliveroo, etc.)</li>
              </ul>

              <h3>Employment Income</h3>
              <ul>
                <li>Salary, bonuses, benefits</li>
                <li>Pension income</li>
                <li>Untaxed tips and gratuities</li>
              </ul>

              <h3>Property Income</h3>
              <ul>
                <li>Rental income from UK property</li>
                <li>Rental income from overseas property</li>
                <li>Airbnb and short-term letting</li>
              </ul>

              <h3>Investment Income</h3>
              <ul>
                <li>Dividends from shares</li>
                <li>Interest from savings</li>
                <li>Income from trusts or estates</li>
              </ul>

              <h3>Capital Gains</h3>
              <ul>
                <li>Profit from selling property (not main home)</li>
                <li>Profit from selling shares or investments</li>
                <li>Profit from selling business assets</li>
              </ul>

              <h2>Allowable Expenses for Self-Employment</h2>

              <p>If you're self-employed, you can deduct business expenses from your income:</p>

              <ul>
                <li>Office costs (rent, utilities, supplies)</li>
                <li>Travel (business mileage, train, hotels)</li>
                <li>Professional fees (accountant, solicitor)</li>
                <li>Marketing and advertising</li>
                <li>Business insurance</li>
                <li>Staff costs (salaries, pensions)</li>
                <li>Stock and materials</li>
              </ul>

              <p className="highlight-note">
                <strong>Rule:</strong> Expenses must be "wholly and exclusively" for business. Mixed-use expenses (e.g., home broadband) can be partly claimed.
              </p>

              <h2>Payment on Account Explained</h2>

              <p>If you owe over £1,000 in tax, HMRC requires you to pay tax in advance for the next year.</p>

              <p><strong>How it works:</strong></p>
              <ul>
                <li>31 January: Pay 50% of last year's tax bill (first payment on account) + any balance from previous year</li>
                <li>31 July: Pay remaining 50% (second payment on account)</li>
                <li>31 January (following year): Pay balance + first payment for new year</li>
              </ul>

              <p>Example: 2025/26 tax bill = £4,000</p>
              <ul>
                <li>31 Jan 2027: Pay £2,000 (first payment on account for 2026/27) + £4,000 balance = £6,000 total</li>
                <li>31 Jul 2027: Pay £2,000 (second payment on account)</li>
              </ul>

              <h2>Common Self Assessment Mistakes</h2>

              <h3>1. Missing Income</h3>
              <p>Failing to declare all income is the most common error. HMRC receives data from employers, banks, and other sources — they will know if you've missed something.</p>

              <h3>2. Incorrect Expense Claims</h3>
              <p>Claiming personal expenses as business = penalties. Only claim legitimate business costs with receipts.</p>

              <h3>3. Late Filing</h3>
              <p>£100 penalty from day 1 of being late. Penalties increase if you're over 3 months late.</p>

              <h3>4. Not Keeping Records</h3>
              <p>You must keep records for 5 years after the 31 January filing deadline. No records = HMRC can estimate your income (usually higher than reality).</p>

              <p>Full guide: <Link to="/blog/record-keeping-uk">Record Keeping Requirements for UK Businesses</Link></p>

              <h2>How Brightson Accounting Can Help</h2>

              <p>We help individuals across Wolverhampton and the West Midlands with:</p>
              <ul>
                <li>Self Assessment registration and setup</li>
                <li>Tax return preparation and filing</li>
                <li>Expense optimization (claiming everything allowable)</li>
                <li>Payment on account calculations</li>
                <li>HMRC enquiry support</li>
              </ul>

              <p>
                Want to stay compliant and <Link to="/corporation-tax-calculator">reduce your corporation tax</Link> at the same time? We can help with both.
              </p>

            </div>

            {/* End CTA */}
            <div className="article-cta-box" style={{ marginTop: '3rem' }}>
              <h2>🚀 Ready to stay compliant and reduce your tax bill?</h2>
              <p>We help businesses across Wolverhampton and the West Midlands:</p>
              <ul>
                <li>Stay compliant with HMRC</li>
                <li>Reduce tax legally</li>
                <li>Avoid penalties and investigations</li>
              </ul>
              <Link to="/contact" className="article-cta-button">👉 Book a Free Consultation</Link>
            </div>

            <div className="related-articles" style={{ marginTop: '3rem' }}>
              <h3>Related Guides</h3>
              <div className="related-links">
                <Link to="/blog/hmrc-compliance-guide-uk" className="related-link">HMRC Compliance Guide</Link>
                <Link to="/blog/hmrc-deadlines-uk" className="related-link">HMRC Deadlines</Link>
                <Link to="/blog/record-keeping-uk" className="related-link">Record Keeping</Link>
                <Link to="/contact" className="related-link">Contact</Link>
              </div>
            </div>
          </div>
        </section>
      </article>
    </>
  );
};

export default SelfAssessmentCompliance;